/** @format */

// File: src/components/relays/RelaySquadList.jsx
// Version: 3.2.2

import { memo } from "@wordpress/element";
import Swal from "sweetalert2";

const RelaySquadList = ({ activeEvent, declaredTeams, setDeclaredTeams, clubRoster, isDeadlinePassed, onOpenLegConfig, onCreateTeam }) => {
    if(!activeEvent) {
        return <div className="alert alert-secondary">No relay events found in competition structure blueprints.</div>;
    }

    const currentTeams = declaredTeams.filter(t => Number(t.scheduleId) === Number(activeEvent.id));

    const handleDeleteSquad = (teamId) => {
        if(isDeadlinePassed()) return;

        Swal.fire({
            title: "Remove Relay Squad?",
            text: "This will dissolve this team draft and delete all assigned runner indices.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete"
        }).then((result) => {
            if(result.isConfirmed) {
                const globalData = window.ammData;
                fetch(`${ globalData.api_url }/relay-teams/${ teamId }`, {
                    method: "DELETE",
                    headers: { "X-WP-Nonce": globalData.nonce }
                })
                    .then(res => res.json())
                    .then(data => {
                        if(data.success) {
                            setDeclaredTeams(prev => prev.filter(t => t.id !== teamId));
                            Swal.fire({
                                title: "Squad Dissolved",
                                text: "The registry payload has been cleared.",
                                icon: "success",
                                timer: 1200,
                                showConfirmButton: false
                            });
                        } else {
                            throw new Error(data.message || "Deletion request rejected.");
                        }
                    })
                    .catch(err => {
                        Swal.fire("Action Blocked", err.message || "Could not clear team array context.", "error");
                    });
            }
        });
    };

    return (
        <div className="bg-white border rounded shadow-sm p-4">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <div>
                    <h5 className="fw-bold mb-1 text-dark">{ activeEvent.disciplineName }</h5>
                    <div className="text-muted small text-uppercase fw-semibold">
                        Division: <span className="text-primary">{ activeEvent.ageClass || "Open" }</span> • Gender Code: <span className="text-primary">{ activeEvent.sexCode || "Mixed" }</span>
                    </div>
                </div>
                { !isDeadlinePassed() && (
                    <button
                        className="btn btn-primary btn-sm px-4 fw-bold shadow-sm rounded-2 d-inline-flex align-items-center gap-1"
                        onClick={ onCreateTeam }
                    >
                        <i className="fas fa-plus-circle"></i> Create Squad Draft
                    </button>
                ) }
            </div>

            { currentTeams.length === 0 ? (
                <div className="text-center py-5 border border-dashed rounded bg-light">
                    <i className="fas fa-users d-block mb-3 text-muted fs-3"></i>
                    <p className="text-muted mb-0 small fw-semibold">No squads declared for this specific pool layout configuration.</p>
                </div>
            ) : (
                <div className="d-flex flex-column gap-3">
                    { currentTeams.map((team, idx) => {
                        const runnersArray = Array.isArray(team.runners) ? team.runners : [];

                        // Segregate primary slots (0-3) and alternate slots (4-7)
                        const primaryRunners = runnersArray.slice(0, 4);
                        const alternateRunners = runnersArray.slice(4, 8);

                        const primaryCount = primaryRunners.filter(Boolean).length;
                        const alternateCount = alternateRunners.filter(Boolean).length;

                        return (
                            <div key={ `declared-squad-row-${ team.id || idx }` } className="p-3 border rounded shadow-xs bg-light d-flex justify-content-between align-items-center transition-all hover-shadow">
                                <div>
                                    <div className="fw-bold text-secondary text-uppercase small" style={ { letterSpacing: "0.3px" } }>
                                        Squad Reference #{ team.id } <span className="text-muted font-normal lowercase ms-1">(Draft Structure)</span>
                                    </div>
                                    <div className="text-muted small mt-1 d-flex flex-wrap gap-2 align-items-center">
                                        <span>
                                            Primary Legs: <span className={ `badge ${ primaryCount === 4 ? "bg-success" : "bg-warning text-dark" } ms-1` }>{ primaryCount } / 4</span>
                                        </span>
                                        <span className="text-light-emphasis opacity-50">•</span>
                                        <span>
                                            Alternates Pool: <span className="badge bg-secondary-subtle text-secondary-emphasis border ms-1">{ alternateCount } / 4</span>
                                        </span>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-outline-dark px-3 fw-medium"
                                        onClick={ () => onOpenLegConfig(team.id) }
                                        style={ { fontSize: "12px" } }
                                    >
                                        { isDeadlinePassed() ? "View Roster Assignment" : "Configure Legs" }
                                    </button>
                                    { !isDeadlinePassed() && (
                                        <button
                                            className="btn btn-sm btn-outline-danger px-3 fw-medium"
                                            onClick={ () => handleDeleteSquad(team.id) }
                                            style={ { fontSize: "12px" } }
                                        >
                                            Delete
                                        </button>
                                    ) }
                                </div>
                            </div>
                        );
                    }) }
                </div>
            ) }
        </div>
    );
};

export default memo(RelaySquadList);